import React, { useState, useRef, useEffect } from 'react';
import './CustomDatePicker.css';

const CustomDatePicker = ({ value, onChange, name, maxDate, className = '', placeholder = 'DD-MM-YYYY' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [showYearPicker, setShowYearPicker] = useState(false);
  const [yearSearch, setYearSearch] = useState('');
  const datePickerRef = useRef(null);
  const yearPickerRef = useRef(null);

  // Parse DD-MM-YYYY format to Date object
  const parseDate = (dateString) => {
    if (!dateString) return null;
    const ddmmyyyyPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    const match = dateString.match(ddmmyyyyPattern);
    if (match) {
      const day = parseInt(match[1], 10);
      const month = parseInt(match[2], 10) - 1; // Month is 0-indexed
      const year = parseInt(match[3], 10);
      return new Date(year, month, day);
    }
    return null;
  };

  // Format Date object to DD-MM-YYYY
  const formatDate = (date) => {
    if (!date || isNaN(date.getTime())) return '';
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  };

  // Convert YYYY-MM-DD or MM-DD-YYYY to DD-MM-YYYY
  const convertToDDMMYYYY = (dateString) => {
    if (!dateString) return '';

    // YYYY-MM-DD format
    const yyyymmddPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
    if (yyyymmddPattern.test(dateString)) {
      const parts = dateString.split('-');
      return `${parts[2]}-${parts[1]}-${parts[0]}`;
    }

    // Already in DD-MM-YYYY format (2-digit-2-digit-4-digit)
    const twoDigitPattern = /^(\d{2})-(\d{2})-(\d{4})$/;
    if (twoDigitPattern.test(dateString)) {
      return dateString;
    }

    return dateString;
  };

  const selectedDate = parseDate(convertToDDMMYYYY(value));

  // Initialize current month/year from selected date or today
  useEffect(() => {
    if (selectedDate) {
      setCurrentMonth(selectedDate.getMonth());
      setCurrentYear(selectedDate.getFullYear());
    }
  }, [value]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setIsOpen(false);
        setShowYearPicker(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Close year picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (yearPickerRef.current && !yearPickerRef.current.contains(event.target)) {
        setShowYearPicker(false);
      }
    };

    if (showYearPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showYearPicker]);


  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  // Get first day of month and number of days
  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const handleDateSelect = (day) => {
    const selected = new Date(currentYear, currentMonth, day);
    let maxDateObj = new Date();

    // Parse maxDate if provided (could be YYYY-MM-DD string or Date object)
    if (maxDate) {
      if (typeof maxDate === 'string') {
        maxDateObj = new Date(maxDate);
      } else {
        maxDateObj = new Date(maxDate);
      }
      // Set to end of day for comparison
      maxDateObj.setHours(23, 59, 59, 999);
    }

    // Check if selected date is in the future
    if (selected > maxDateObj) {
      return;
    }

    const formattedDate = formatDate(selected);
    onChange({ target: { name, value: formattedDate } });
    setIsOpen(false);
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handlePrevYear = () => {
    setCurrentYear(currentYear - 1);
  };

  const handleNextYear = () => {
    setCurrentYear(currentYear + 1);
  };

  const handleYearSelect = (year) => {
    setCurrentYear(year);
    setShowYearPicker(false);
  };

  // Generate list of years (100 years back from current year)
  const generateYearList = () => {
    const currentYearValue = new Date().getFullYear();
    const years = [];
    const startYear = currentYearValue - 100;

    for (let year = currentYearValue; year >= startYear; year--) {
      years.push(year);
    }

    // Filter by search if provided
    if (yearSearch) {
      return years.filter(year => year.toString().includes(yearSearch));
    }

    return years;
  };

  // Reset year search when year picker closes
  useEffect(() => {
    if (!showYearPicker) {
      setYearSearch('');
    }
  }, [showYearPicker]);

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentMonth === today.getMonth() &&
      currentYear === today.getFullYear()
    );
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      currentMonth === selectedDate.getMonth() &&
      currentYear === selectedDate.getFullYear()
    );
  };

  const isDisabled = (day) => {
    const date = new Date(currentYear, currentMonth, day);
    let maxDateObj = new Date();

    // Parse maxDate if provided
    if (maxDate) {
      if (typeof maxDate === 'string') {
        maxDateObj = new Date(maxDate);
      } else {
        maxDateObj = new Date(maxDate);
      }
      // Set to end of day for comparison
      maxDateObj.setHours(23, 59, 59, 999);
    }

    return date > maxDateObj;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="calendar-day empty"></div>);
    }

    // Add cells for each day of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const disabled = isDisabled(day);
      const today = isToday(day);
      const selected = isSelected(day);

      days.push(
        <div
          key={day}
          className={`calendar-day ${disabled ? 'disabled' : ''} ${today ? 'today' : ''} ${selected ? 'selected' : ''}`}
          onClick={() => !disabled && handleDateSelect(day)}
        >
          {day}
        </div>
      );
    }

    return days;
  };

  const displayValue = convertToDDMMYYYY(value) || '';

  return (
    <div className={`custom-date-picker ${className}`} ref={datePickerRef}>
      <div
        className={`date-picker-input ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <input
          type="text"
          readOnly
          value={displayValue}
          placeholder={placeholder}
          className="date-input-readonly"
        />
        <i className="fas fa-calendar-alt calendar-icon"></i>
      </div>
      {isOpen && (
        <div className="date-picker-calendar">
          <div className="calendar-header">
            <div className="calendar-nav">
              <button
                type="button"
                className="nav-button"
                onClick={handlePrevYear}
                title="Previous Year"
              >
                <i className="fas fa-angle-double-left"></i>
              </button>
              <button
                type="button"
                className="nav-button"
                onClick={handlePrevMonth}
                title="Previous Month"
              >
                <i className="fas fa-angle-left"></i>
              </button>
            </div>
            <div className="calendar-month-year">
              <span className="calendar-month">{monthNames[currentMonth]}</span>
              <span
                className="calendar-year-selectable"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowYearPicker(!showYearPicker);
                }}
                title="Click to select year"
              >
                {currentYear}
                <i className="fas fa-chevron-down year-dropdown-icon"></i>
              </span>
              {showYearPicker && (
                <div className="year-picker-dropdown" ref={yearPickerRef}>
                  <div className="year-picker-header">
                    <input
                      type="text"
                      className="year-search-input"
                      placeholder="Search year..."
                      value={yearSearch}
                      onClick={(e) => e.stopPropagation()}
                      onChange={(e) => {
                        e.stopPropagation();
                        setYearSearch(e.target.value);
                      }}
                    />
                  </div>
                  <div className="year-picker-list">
                    {generateYearList().length > 0 ? (
                      generateYearList().map((year) => (
                        <div
                          key={year}
                          className={`year-picker-item ${year === currentYear ? 'selected' : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleYearSelect(year);
                          }}
                        >
                          {year}
                        </div>
                      ))
                    ) : (
                      <div className="year-picker-no-results">No years found</div>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="calendar-nav">
              <button
                type="button"
                className="nav-button"
                onClick={handleNextMonth}
                title="Next Month"
              >
                <i className="fas fa-angle-right"></i>
              </button>
              <button
                type="button"
                className="nav-button"
                onClick={handleNextYear}
                title="Next Year"
              >
                <i className="fas fa-angle-double-right"></i>
              </button>
            </div>
          </div>
          <div className="calendar-weekdays">
            {dayNames.map((day) => (
              <div key={day} className="calendar-weekday">
                {day}
              </div>
            ))}
          </div>
          <div className="calendar-days">
            {renderCalendar()}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;
